// Local UI preferences (favorites, recently used). These are item IDs only — metadata,
// not secrets — so localStorage is acceptable (§20 forbids secrets there, not ids).
import type { ItemFilter } from "./items";

const FAV = "vault.favorites";
const RECENT = "vault.recent";
const UI = "vault.ui";
const SEARCHES = "vault.searches";
const RECENT_MAX = 20;

function load(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}
function save(key: string, ids: string[]) {
  try {
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

export function getFavorites(): Set<string> {
  return new Set(load(FAV));
}
export function toggleFavorite(id: string): Set<string> {
  const s = new Set(load(FAV));
  if (s.has(id)) s.delete(id);
  else s.add(id);
  save(FAV, [...s]);
  return s;
}
export function getRecent(): string[] {
  return load(RECENT);
}
export function pushRecent(id: string) {
  const next = [id, ...load(RECENT).filter((x) => x !== id)].slice(0, RECENT_MAX);
  save(RECENT, next);
}

// Workspace UI preferences (layout/behaviour, never secrets). Phase 5 will add
// idleLockMin / clipboardClearSec here so the settings screen has one place to read.
export interface UiPrefs {
  navCollapsed: boolean;
}
const UI_DEFAULTS: UiPrefs = { navCollapsed: false };

export function getUiPrefs(): UiPrefs {
  try {
    const raw = localStorage.getItem(UI);
    return raw ? { ...UI_DEFAULTS, ...(JSON.parse(raw) as Partial<UiPrefs>) } : { ...UI_DEFAULTS };
  } catch {
    return { ...UI_DEFAULTS };
  }
}
export function setUiPrefs(patch: Partial<UiPrefs>): UiPrefs {
  const next = { ...getUiPrefs(), ...patch };
  try {
    localStorage.setItem(UI, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

// Saved searches (UX phase 3) — named faceted filters, per user in localStorage. A filter is just
// type/tag/date/text selectors (no secrets), so localStorage is fine. Kept client-side (not on the
// server) since it's a personal convenience, unlike templates which are shared per org.
export interface SavedSearch {
  id: string;
  name: string;
  filter: ItemFilter;
}

export function getSavedSearches(): SavedSearch[] {
  try {
    const raw = localStorage.getItem(SEARCHES);
    return raw ? (JSON.parse(raw) as SavedSearch[]) : [];
  } catch {
    return [];
  }
}
function writeSearches(list: SavedSearch[]): SavedSearch[] {
  try {
    localStorage.setItem(SEARCHES, JSON.stringify(list));
  } catch {
    /* ignore */
  }
  return list;
}
export function addSavedSearch(name: string, filter: ItemFilter): SavedSearch[] {
  const id = crypto.randomUUID();
  return writeSearches([...getSavedSearches(), { id, name, filter }]);
}
export function removeSavedSearch(id: string): SavedSearch[] {
  return writeSearches(getSavedSearches().filter((s) => s.id !== id));
}

// Security preferences (UX phase 5) — auto-lock idle timeout (minutes) and clipboard-clear delay
// (seconds), configurable in the settings screen and enforced app-side (§20). Per device/user in
// localStorage (policy flags, never secrets). Clamped to sane bounds on read so a hand-edited or
// stale value can't disable the protections.
const SECURITY = "vault.security";
export interface SecurityPrefs {
  idleLockMin: number;
  clipboardClearSec: number;
  /** Grace before a hidden tab locks (seconds). 0 = lock immediately, as it did before. */
  hideLockGraceSec: number;
}
const SECURITY_DEFAULTS: SecurityPrefs = { idleLockMin: 15, clipboardClearSec: 30, hideLockGraceSec: 60 };
const clamp = (n: number, lo: number, hi: number, fallback: number): number =>
  Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : fallback;

export function getSecurityPrefs(): SecurityPrefs {
  let raw: Partial<SecurityPrefs> = {};
  try {
    const s = localStorage.getItem(SECURITY);
    if (s) raw = JSON.parse(s) as Partial<SecurityPrefs>;
  } catch {
    /* ignore */
  }
  return {
    idleLockMin: clamp(raw.idleLockMin ?? SECURITY_DEFAULTS.idleLockMin, 1, 240, SECURITY_DEFAULTS.idleLockMin),
    clipboardClearSec: clamp(raw.clipboardClearSec ?? SECURITY_DEFAULTS.clipboardClearSec, 5, 600, SECURITY_DEFAULTS.clipboardClearSec),
    hideLockGraceSec: clamp(raw.hideLockGraceSec ?? SECURITY_DEFAULTS.hideLockGraceSec, 0, 300, SECURITY_DEFAULTS.hideLockGraceSec),
  };
}
export function setSecurityPrefs(patch: Partial<SecurityPrefs>): SecurityPrefs {
  const next = { ...getSecurityPrefs(), ...patch };
  try {
    localStorage.setItem(SECURITY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return getSecurityPrefs(); // re-read so the result is clamped
}

// Restore-open-tabs (desktop comfort) — whether to reopen the previous session tabs on next launch,
// plus the descriptor list of those tabs. Only item IDs + kind are stored (§20 — never adhoc launch
// params or secrets), so localStorage is acceptable. Only item-backed tabs are restorable.
const RESTORE_TABS = "vault.restoreTabs";
const OPEN_TABS = "vault.tabs";
export interface OpenTabRef {
  itemId: string;
  kind: string; // SessionKind, e.g. "view" | "ssh" | "rdp"
}
export function getRestoreTabs(): boolean {
  try {
    return localStorage.getItem(RESTORE_TABS) === "1";
  } catch {
    return false;
  }
}
export function setRestoreTabs(on: boolean): void {
  try {
    localStorage.setItem(RESTORE_TABS, on ? "1" : "0");
  } catch {
    /* ignore */
  }
}
export function getOpenTabs(): OpenTabRef[] {
  try {
    const raw = localStorage.getItem(OPEN_TABS);
    return raw ? (JSON.parse(raw) as OpenTabRef[]) : [];
  } catch {
    return [];
  }
}
export function setOpenTabs(list: OpenTabRef[]): void {
  try {
    localStorage.setItem(OPEN_TABS, JSON.stringify(list));
  } catch {
    /* ignore */
  }
}

// RDP renderer preference — whether RDP items open in the in-app IronRDP viewer (default) or the
// native OS client. A per-item "RDP client" field overrides this global default. Non-secret policy
// flag, read synchronously when launching, so it lives in localStorage.
const RDP_NATIVE = "vault.rdp.native";
export function getRdpUseNative(): boolean {
  try {
    return localStorage.getItem(RDP_NATIVE) === "1";
  } catch {
    return false;
  }
}
export function setRdpUseNative(useNative: boolean): void {
  try {
    localStorage.setItem(RDP_NATIVE, useNative ? "1" : "0");
  } catch {
    /* ignore */
  }
}
